import { isUUID } from '../uuid/isUUID';

/**
 * Encoding/decoding for the OAuth `state` parameter used by the federated (SSO) login flow.
 *
 * The Cognito app client is shared across tenants and we cannot register a per-tenant callback
 * URL, so `redirect_uri` has to stay the bare origin. The tenant therefore travels in `state`,
 * which survives the Cognito round-trip even in browsers that drop sessionStorage on the way
 * back (HRI2-16956 - Safari).
 *
 * The tenant code being readable in `state` is fine: it normally sits in the URL path anyway.
 * Opacity comes from the nonce, and integrity from the nonce -> tenant binding we persist
 * locally before redirecting (see `ssoStateStorage`).
 *
 * This module is deliberately pure: no storage access, no environment reads.
 */

/** Schema version of the encoded payload. Anything else decodes to `null`. */
export const oauthStateVersion = 1;

export type OAuthState = {
  version: typeof oauthStateVersion;
  tenantId: string;
  nonce: string;
};

/** Wire format. Kept short because it travels in a URL. */
type OAuthStatePayload = {
  v: number;
  t: string;
  n: string;
};

const nonceLength = 32;
const isNonce = (value: string) => !!/^[0-9a-f]{32}$/.exec(value);

/**
 * Generates the single-use nonce that binds an authorize request to its callback.
 *
 * `crypto.randomUUID` is deliberately avoided: it is gated on a secure context and would throw
 * where `getRandomValues` still works.
 */
export function createOAuthStateNonce(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(nonceLength / 2));
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Mirrors the tenant shapes accepted by the `:tenantId` route (tenant code, training delivery
 * path or UUID). Duplicated rather than imported so that decoding stays total - the router's
 * own validator throws.
 */
export function isValidStateTenantId(tenantId: string): boolean {
  const isSegmentTenantCode = /^[A-Z0-9]{4}$/.exec(tenantId);
  const isSegmentTrainingDeliveryPath = /^[a-z0-9]{4,25}$/.exec(tenantId);
  const isSegmentUUID = isUUID(tenantId);

  return !!isSegmentTenantCode || !!isSegmentTrainingDeliveryPath || isSegmentUUID;
}

/**
 * Encodes the tenant/nonce pair as `base64url(JSON)`.
 * The result still needs `encodeURIComponent` when appended to a URL.
 *
 * Encoding enforces what decoding requires. Otherwise a tenant code outside the accepted shapes
 * would mint a `state` that every callback then rejects - an unrecoverable sign-in loop. Throwing
 * here instead reports the real cause through login()'s existing error path, before any redirect.
 */
export function encodeOAuthState({ tenantId, nonce }: { tenantId: string; nonce: string }): string {
  if (!isValidStateTenantId(tenantId)) {
    throw new Error(`Cannot encode OAuth state: ${tenantId} is not a usable tenant identifier.`);
  }
  if (!isNonce(nonce)) {
    throw new Error('Cannot encode OAuth state: the nonce is malformed.');
  }

  const payload: OAuthStatePayload = { v: oauthStateVersion, t: tenantId, n: nonce };
  return toBase64Url(JSON.stringify(payload));
}

/**
 * Decodes a `state` value received from the identity provider.
 *
 * `state` is fully untrusted input, so this never throws: anything malformed, of an unknown
 * schema version or carrying an implausible tenant/nonce decodes to `null`, which callers treat
 * as "no state was present".
 */
export function decodeOAuthState(raw: string | null | undefined): OAuthState | null {
  if (!raw) {
    return null;
  }

  try {
    const payload: unknown = JSON.parse(fromBase64Url(raw));

    if (typeof payload !== 'object' || payload === null) {
      return null;
    }

    const { v, t, n } = payload as Partial<OAuthStatePayload>;

    if (v !== oauthStateVersion || typeof t !== 'string' || typeof n !== 'string') {
      return null;
    }
    if (!isValidStateTenantId(t) || !isNonce(n)) {
      return null;
    }

    return { version: oauthStateVersion, tenantId: t, nonce: n };
  } catch {
    return null;
  }
}

function toBase64Url(value: string): string {
  return btoa(value).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
}

function fromBase64Url(value: string): string {
  const base64 = value.replaceAll('-', '+').replaceAll('_', '/');
  const padding = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
  return atob(base64 + padding);
}
